import { Device } from '@twilio/voice-sdk';
import { isUpcomingFeaturesEnabled } from '@ergeon/erg-utils-js';

import { API_HOST, SHOW_UPCOMING_FEATURES } from './envs';
import { logService, settingsService } from './services';

export const formatSecondsToTimeString = (seconds: number): string /* 00:00 */ => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return [
    hours > 0 ? String(hours).padStart(2, '0') : null,
    String(minutes).padStart(2, '0'),
    String(secs).padStart(2, '0'),
  ]
    .filter(Boolean)
    .join(':');
};

export const isErrorLog = (log: string) => (/error/ig).test(log);

/**
 * Parse URL query parameters
 * @returns Object with parameter keys and values
 */
export const getUrlParams = (): Record<string, string> => {
  const params = new URLSearchParams(window.location.search);
  const result: Record<string, string> = {};

  for (const [key, value] of params.entries()) {
    result[key] = value;
  }

  return result;
};

type getAdminLinkArgs = {
  app: string;
  model: string;
  id?: number;
  admin?: string
}

export const getAdminLink = ({ app, model, id, admin = 'public' }: getAdminLinkArgs) => {
  if (id) {
    return `${API_HOST}/${admin}-admin/${app}/${model}/${id}/change`;
  }

  return `${API_HOST}/${admin}-admin/${app}/${model}/`;
};

export const applyDeviceSettings = async(deviceInstance: Device) => {
  const settings = settingsService.getSettings();

  if (settings.microphoneId) {
    try {
      await deviceInstance.audio?.setInputDevice(settings.microphoneId);
      logService.addLog(`Microphone device set successfully: ${settings.microphoneId}`);
    } catch (err) {
      const errorMsg = `Failed to set microphone device: ${err}`;
      logService.addLog(errorMsg);
      console.error('setInputDevice error:', err);
    }
  }

  if (settings.speakerId) {
    try {
      await deviceInstance.audio?.speakerDevices.set(settings.speakerId);
      logService.addLog('Speaker device is changed successfully');
    } catch (err) {
      const errorMsg = `Failed to set speaker device: ${err}`;
      logService.addLog(errorMsg);
      console.error('speakerDevices error:', err);
    }

    try {
      await deviceInstance.audio?.ringtoneDevices.set(settings.speakerId);
      logService.addLog('Ringtone device is changed successfully');
    } catch (err) {
      const errorMsg = `Failed to set ringtone device: ${err}`;
      logService.addLog(errorMsg);
      console.error('ringtoneDevices error:', err);
    }
  }
};

export const showUpcomingFeatures = (issueNumber: string) => {
  return isUpcomingFeaturesEnabled(issueNumber, undefined, String(SHOW_UPCOMING_FEATURES));
};

// Re-export appStorage functions for convenience
export {
  getActivitiesFilter,
  setActivitiesFilter,
  getActivitiesImportantOnly,
  setActivitiesImportantOnly,
  clearActivitiesFilters,
} from './utils/appStorage';
